// Centralized color palette derived from theme.
import { getTheme } from 'config/theme';
import { getSiloKeyFromWindowUrl } from 'platform-specific/url';
import { getDeploymentConfig } from 'util/util';

const theme = getTheme();
const c = theme?.colors;

const siloKey = getSiloKeyFromWindowUrl();
const deploymentConfig = getDeploymentConfig();
const siloConfig = deploymentConfig?.siloConfig?.[siloKey];

// Text colors
export const colorTextPrimary = c?.colorTextPrimary ?? '#000000';
export const colorTextSecondaryOnBanner = c?.colorTextSecondaryOnBanner ?? '#2B2B2B';
export const colorTextSecondary = c?.colorTextSecondary ?? '#65676B';
export const colorTextDisabled = c?.colorTextDisabled ?? '#818181';
export const colorTextHint = c?.colorTextHint ?? '#818181';
export const colorTextAccent = c?.colorTextAccent ?? '#5A1EF5';
export const colorTextWarning = c?.colorTextWarning ?? '#F53C19';
export const colorTextSuccess = c?.colorTextSuccess ?? '#00af87';
export const colorTextOnPrimaryButton = c?.colorTextOnPrimaryButton ?? '#ffffff';
export const colorTextOnAccentColorBg = c?.colorTextOnAccentColorBg ?? '#ffffff';
export const colorTextOnEntryPointColorBg = c?.colorTextOnEntryPointColorBg ?? '#ffffff';
export const colorTextOnTagLight = c?.colorTextOnTagLight ?? '#000000';
export const colorTextOnToastDark = c?.colorTextOnToastDark ?? '#ffffff';

// Button colors
export const colorButtonPrimary = c?.colorButtonPrimary ?? '#0F0F0F';
export const colorButtonAccent = c?.colorButtonAccent ?? '#5A1EF5';
export const colorButtonSecondary = c?.colorButtonSecondary ?? '#ffffff';
export const colorButtonDisabled = c?.colorButtonDisabled ?? '#E9E9E9';
export const colorButtonSliderThumb = c?.colorButtonSliderThumb ?? '#FFFFFF';

// Surface
export const colorSurfacePrimary = c?.colorSurfacePrimary ?? '#FFFFFF';
export const colorSurfaceSecondary = c?.colorSurfaceSecondary ?? '#F7F7F7';
export const colorSurfaceHighlight = c?.colorSurfaceHighlight ?? '#FEF7F2';
export const colorSurfaceHighlightShade1 = c?.colorSurfaceHighlightShade1 ?? '#F6F0EC';
export const colorSurfaceHighlightShade2 = c?.colorSurfaceHighlightShade2 ?? '#F6F0EC';
export const colorSurfaceBannerWarning = c?.colorSurfaceBannerWarning ?? '#FCEBEA';
export const colorSurfaceBannerSuggestion = c?.colorSurfaceBannerSuggestion ?? '#FEF7F2';
export const colorSurfaceBannerSuccess = c?.colorSurfaceBannerSuccess ?? '#B9EBDC';
export const colorSurfaceBannerInfo = c?.colorSurfaceBannerInfo ?? '#F5F9FF';
export const colorSurfaceUserBlock = c?.colorSurfaceUserBlock ?? '#F53C19';
export const colorSurfaceUserWarning = c?.colorSurfaceUserWarning ?? '#FFC841';
export const colorSurfaceUserUnblock = c?.colorSurfaceUserUnblock ?? '#00AF87';
export const colorSurfaceUserVerified = c?.colorSurfaceUserVerified ?? '#CDE1FF';
export const colorSurfaceToastDark = c?.colorSurfaceToastDark ?? '#0F0F0F';

// Tag
export const colorTagDefault = c?.colorTagDefault ?? '#F7F7F7';
export const colorTagAccent = c?.colorTagAccent ?? '#5A1EF5';
export const colorTagAccentLight = c?.colorTagAccentLight ?? '#ECECFF';
export const colorTagHighlight = c?.colorTagHighlight ?? '#FCEBEA';
export const colorTagWarning = c?.colorTagWarning ?? '#FCEBEA';
export const colorTagSuccess = c?.colorTagSuccess ?? '#B9EBDC';
export const colorTagVerified = c?.colorTagVerified ?? '#CDE1FF';
export const colorTagWhite = c?.colorTagWhite ?? '#FFFFFF';

// Border
export const colorBorderPrimary = c?.colorBorderPrimary ?? '#d9d9d9';
export const colorBorderPrimaryOnBanner = c?.colorBorderPrimaryOnBanner ?? '#0F0F0F33';
export const colorBorderSelected = c?.colorBorderSelected ?? '#0F0F0F';
export const colorBorderAccent = c?.colorBorderAccent ?? '#5A1EF5';
export const colorBorderAccentLight = c?.colorBorderAccentLight ?? '#ECECFF';
export const colorBorderWarning = c?.colorBorderWarning ?? '#F53C19';
export const colorBorderFacepile = c?.colorBorderFacepile ?? '#FFFFFF';

// Data Viz
export const colorDataVizScale1 = c?.colorDataVizScale1 ?? '#5A1EF5';
export const colorDataVizScale2 = c?.colorDataVizScale2 ?? '#7265FF';
export const colorDataVizScale3 = c?.colorDataVizScale3 ?? '#ECECFF';
export const colorDataVizOther = c?.colorDataVizOther ?? '#DCE0E5';
export const colorDataVizCategory1 = c?.colorDataVizCategory1 ?? '#5A1EF5';
export const colorDataVizCategory2 = c?.colorDataVizCategory2 ?? '#BE73CD';
export const colorDataVizCategory3 = c?.colorDataVizCategory3 ?? '#058287';
export const colorDataVizCategory4 = c?.colorDataVizCategory4 ?? '#763A35';
export const colorDataVizCategory5 = c?.colorDataVizCategory5 ?? '#C34646';

// Entry point colors
export const colorEntryPointColorGreen = c?.colorEntryPointColorGreen ?? '#058287';
export const colorEntryPointColorGreenTint = c?.colorEntryPointColorGreenTint ?? '#91D7BE';
export const colorEntryPointColorBrown = c?.colorEntryPointColorBrown ?? '#A95851';
export const colorEntryPointColorBrownTint = c?.colorEntryPointColorBrownTint ?? '#B57A75';
export const colorEntryPointColorRed = c?.colorEntryPointRed ?? '#C34646';
export const colorEntryPointColorRedTint = c?.colorEntryPointColorRedTint ?? '#EB7369';
export const colorEntryPointColorPurple = c?.colorEntryPointColorPurple ?? '#3F3F80';
export const colorEntryPointColorPurpleTint = c?.colorEntryPointColorPurpleTint ?? '#7F7Fc0';
export const colorEntryPointColorBlue = c?.colorEntryPointColorBlue ?? '#525DCF';
export const colorEntryPointColorBlueTint = c?.colorEntryPointColorBlueTint ?? '#90AACF';

// Default avatar colors
export const jasper1 = c?.jasper1 ?? '#7d6453';
export const garnet1 = c?.garnet1 ?? '#763a35';
export const garnet2 = c?.garnet2 ?? '#b57a75';
export const beetle = c?.beetle ?? '#a95851';
export const carnation1 = c?.carnation1 ?? '#c34646';
export const kilauea1 = c?.kilauea1 ?? '#F53C19';
export const elsinore1 = c?.elsinore1 ?? '#f56400';
export const saguaro1 = c?.saguaro1 ?? '#a57328';
export const hoh1 = c?.hoh1 ?? '#058287';
export const pisgah1 = c?.pisgah1 ?? '#553cd7';
export const timucuan1 = c?.timucuan1 ?? '#be73cd';
export const tanzanite1 = c?.tanzanite1 ?? '#3f3f80';
export const tanzanite2 = c?.tanzanite2 ?? '#7f7fc0';
export const emperor = c?.emperor ?? '#525dcf';
export const lavender = c?.lavender ?? '#7d7dff';
export const defaultAvatarColors = siloConfig?.defaultAvatarColors ?? [
    jasper1,
    garnet1,
    garnet2,
    beetle,
    carnation1,
    kilauea1,
    elsinore1,
    saguaro1,
    hoh1,
    pisgah1,
    timucuan1,
    tanzanite1,
    tanzanite2,
    emperor,
    lavender,
];

// Primary Brand
export const colorPrimaryBrandWhite = c?.colorPrimaryBrandWhite ?? '#FFFFFF';
export const colorPrimaryBrandBlack = c?.colorPrimaryBrandBlack ?? '#000000';
export const colorPrimaryBrandIris = c?.colorPrimaryBrandIris ?? '#5A1EF5';
export const colorPrimaryBrandLavender = c?.colorPrimaryBrandLavender ?? '#7265FF';
export const colorPrimaryBrandTanzanite3 = c?.colorPrimaryBrandTanzanite3 ?? '#ECECFF';

// Secondary Brand
export const colorSecondaryBrandJasper3 = c?.colorSecondaryBrandJasper3 ?? '#FEF7F2';
export const colorSecondaryBrandJasper3Shade1 = c?.colorSecondaryBrandJasper3Shade1 ?? '#F6F0EC';
export const colorSecondaryBrandJasper3Shade2 = c?.colorSecondaryBrandJasper3Shade2 ?? '#EDE4DE';

// Utility Colors
export const colorUtilityKilauea1 = c?.colorUtilityKilauea1 ?? '#F53C19';
export const colorUtilityKilauea2 = c?.colorUtilityKilauea2 ?? '#FA7355';
export const colorUtilityGarnet3 = c?.colorUtilityGarnet3 ?? '#FCEBEA';
export const colorUtilitySaguaro2 = c?.colorUtilitySaguaro2 ?? '#FFC841';
export const colorUtilitySaguaro3 = c?.colorUtilitySaguaro3 ?? '#FFE6B4';
export const colorUtilityMum = c?.colorUtilityMum ?? '#00AF87';
export const colorUtilityHoh3 = c?.colorUtilityHoh3 ?? '#B9EBDC';
export const colorUtilityHydrangea1 = c?.colorUtilityHydrangea1 ?? '#CDE1FF';
export const colorUtilityHydrangea3 = c?.colorUtilityHydrangea3 ?? '#F5F9FF';
export const colorUtilityGreyBlue = c?.colorUtilityGreyBlue ?? '#DCE0E5';

// Greyscale
export const colorGrey1 = c?.colorGrey1 ?? '#F7F7F7';
export const colorGrey2 = c?.colorGrey2 ?? '#E9E9E9';
export const colorGrey3 = c?.colorGrey3 ?? '#D9D9D9';
export const colorGrey4 = c?.colorGrey4 ?? '#818181';
export const colorGrey5 = c?.colorGrey5 ?? '#65676B';
export const colorGrey6 = c?.colorGrey6 ?? '#2B2B2B';
export const colorSoftBlack = c?.colorSoftBlack ?? '#0F0F0F';

// Extended Color Palette
export const colorExtendedJasper1 = c?.colorExtendedJasper1 ?? '#7D6453';
export const colorExtendedGarnet1 = c?.colorExtendedGarnet1 ?? '#763A35';
export const colorExtendedGarnet2 = c?.colorExtendedGarnet2 ?? '#B57A75';
export const colorExtendedBeetle = c?.colorExtendedBeetle ?? '#A95851';
export const colorExtendedCarnation1 = c?.colorExtendedCarnation1 ?? '#C34646';
export const colorExtendedCarnation2 = c?.colorExtendedCarnation2 ?? '#EB7369';
export const colorExtendedElsinore1 = c?.colorExtendedElsinore1 ?? '#F56400';
export const colorExtendedSaguaro1 = c?.colorExtendedSaguaro1 ?? '#A57328';
export const colorExtendedHoh1 = c?.colorExtendedHoh1 ?? '#058287';
export const colorExtendedHoh2 = c?.colorExtendedHoh2 ?? '#91D7BE';
export const colorExtendedPisgah1 = c?.colorExtendedPisgah1 ?? '#553CD7';
export const colorExtendedTimucuan1 = c?.colorExtendedTimucuan1 ?? '#BE73CD';
export const colorExtendedTanzanite1 = c?.colorExtendedTanzanite1 ?? '#3F3F80';
export const colorExtendedTanzanite2 = c?.colorExtendedTanzanite2 ?? '#7F7FC0';
export const colorExtendedEmperor = c?.colorExtendedEmperor ?? '#525DCF';
export const colorExtendedGossamer = c?.colorExtendedGossamer ?? '#90AACF';

// Illustration Palette 1
export const colorIllustration1Jasper3 = c?.colorIllustration1Jasper3 ?? '#FEF7F2';
export const colorIllustration1Jasper3Shade1 = c?.colorIllustration1Jasper3Shade1 ?? '#F6F0EC';
export const colorIllustration1Hydrangea3 = c?.colorIllustration1Hydrangea3 ?? '#F5F9FF';
export const colorIllustration1Hydrangea3Shade = c?.colorIllustration1Hydrangea3Shade ?? '#E8EFF9';
export const colorIllustration1Kilauea1 = c?.colorIllustration1Kilauea1 ?? '#F53C19';
export const colorIllustration1Kilauea3 = c?.colorIllustration1Kilauea3 ?? '#FFB4A5';
export const colorIllustration1Hoh1 = c?.colorIllustration1Hoh1 ?? '#058287';
export const colorIllustration1Tanzanite1 = c?.colorIllustration1Tanzanite1 ?? '#3F3F80';

// Illustration Palette 2
export const colorIllustration2Emperor = c?.colorIllustration2Emperor ?? '#525DCF';
export const colorIllustration2Hydrangea1 = c?.colorIllustration2Hydrangea1 ?? '#CDE1FF';
export const colorIllustration2Hoh1 = c?.colorIllustration2Hoh1 ?? '#058287';
export const colorIllustration2Mum = c?.colorIllustration2Mum ?? '#00AF87';
export const colorIllustration2Hoh2 = c?.colorIllustration2Hoh2 ?? '#91D7BE';
export const colorIllustration2Garnet2 = c?.colorIllustration2Garnet2 ?? '#B57A75';
export const colorIllustration2Carnation1 = c?.colorIllustration2Carnation1 ?? '#C34646';
export const colorIllustration2Carnation3 = c?.colorIllustration2Carnation3 ?? '#F07D7D';
export const colorIllustration2Kilauea2 = c?.colorIllustration2Kilauea2 ?? '#FA7355';
export const colorIllustration2Saguaro2 = c?.colorIllustration2Saguaro2 ?? '#FFC841';
